import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface AnimatedCardProps {
  children: React.ReactNode;
  className?: string;
  hoverEffect?: boolean;
  animationDelay?: number;
  animationType?: 'fade-in' | 'fade-in-up' | 'fade-in-left' | 'fade-in-right' | 'scale';
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

const AnimatedCard = ({
  children,
  className,
  hoverEffect = true,
  animationDelay = 0,
  animationType = 'fade-in-up',
  onMouseEnter,
  onMouseLeave,
}: AnimatedCardProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.disconnect();
      }
    };
  }, []);

  return (
    <div
      ref={cardRef}
      className={cn(
        'rounded-2xl p-6 glass-card transition-opacity duration-500',
        isVisible ? `animate-${animationType} opacity-100` : 'opacity-0',
        animationDelay > 0 && `animate-delay-${animationDelay}`,
        hoverEffect && 'interactive-card',
        className
      )}
      style={{ 
        animationFillMode: 'forwards',
        animationPlayState: isVisible ? 'running' : 'paused'
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </div>
  );
};

export default AnimatedCard;
